import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { Close, Email, Lock, CheckCircle } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import authService from '../shared/services/authService';

interface ForgotPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

// Step 1: Request reset code
const emailSchema = yup.object({
  email: yup.string().email('Invalid email address').required('Email is required'),
});

// Step 2: Verify code and set new password
const resetSchema = yup.object({
  code: yup.string().required('Verification code is required').min(4, 'Code must be at least 4 characters'),
  newPassword: yup.string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number'),
  confirmPassword: yup.string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

interface EmailFormData {
  email: string;
}

interface ResetFormData {
  code: string;
  newPassword: string;
  confirmPassword: string;
}

const steps = ['Request Reset Code', 'Verify & Reset Password', 'Complete'];

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  // Step 1 form
  const {
    control: emailControl,
    handleSubmit: handleEmailSubmit,
    reset: resetEmailForm,
    formState: { errors: emailErrors },
  } = useForm<EmailFormData>({
    resolver: yupResolver(emailSchema),
    defaultValues: { email: '' },
  });

  // Step 2 form
  const {
    control: resetControl,
    handleSubmit: handleResetSubmit,
    reset: resetResetForm,
    formState: { errors: resetErrors },
  } = useForm<ResetFormData>({
    resolver: yupResolver(resetSchema),
    defaultValues: { code: '', newPassword: '', confirmPassword: '' },
  });

  const handleClose = () => {
    setActiveStep(0);
    setEmail('');
    setError('');
    resetEmailForm();
    resetResetForm();
    onClose();
  };

  const onEmailSubmit = async (data: EmailFormData) => {
    try {
      setIsLoading(true);
      setError('');
      
      await authService.requestPasswordReset(data.email);
      
      setEmail(data.email);
      setActiveStep(1);
      toast.success('Reset code sent to your email!');
    } catch (error: any) {
      console.error('Password reset request error:', error);
      
      let errorMessage = 'Failed to send reset code. Please try again.';
      
      if (error.response?.status === 404) {
        errorMessage = 'Email address not found. Please check and try again.';
      } else if (error.response?.status === 429) {
        errorMessage = 'Too many requests. Please wait before trying again.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const onResetSubmit = async (data: ResetFormData) => {
    try {
      setIsLoading(true);
      setError('');
      
      await authService.resetPassword(data.code, data.newPassword);
      
      setActiveStep(2);
      toast.success('Password reset successfully!');
      
      // Auto close after success
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      let errorMessage = 'Failed to reset password. Please try again.';
      
      if (error.response?.status === 400) {
        errorMessage = 'Invalid or expired reset code. Please request a new one.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Reset code not found. Please request a new reset code.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <form onSubmit={handleEmailSubmit(onEmailSubmit)}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Box display="flex" justifyContent="center" mb={2}>
                <Email sx={{ fontSize: 48, color: theme.palette.primary.main }} />
              </Box>
              
              <Typography variant="body2" color="textSecondary" textAlign="center">
                Enter your email address and we'll send you a verification code to reset your password.
              </Typography>

              {error && (
                <Alert severity="error">{error}</Alert>
              )}

              <Controller
                name="email"
                control={emailControl}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    type="email"
                    variant="outlined"
                    fullWidth
                    error={!!emailErrors.email}
                    helperText={emailErrors.email?.message}
                    disabled={isLoading}
                    autoFocus
                  />
                )}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : <Email />}
              >
                {isLoading ? 'Sending...' : 'Send Reset Code'}
              </Button>
            </Box>
          </form>
        );

      case 1:
        return (
          <form onSubmit={handleResetSubmit(onResetSubmit)} autoComplete="off">
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Hidden dummy fields to prevent autofill */}
              <input
                type="email"
                name="email_fake"
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
              />
              <input
                type="text"
                name="code_fake"
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
              />

              <Box display="flex" justifyContent="center" mb={2}>
                <Lock sx={{ fontSize: 48, color: theme.palette.primary.main }} />
              </Box>

              <Typography variant="body2" color="textSecondary" textAlign="center">
                We've sent a verification code to <strong>{email}</strong>.
                Enter the code and your new password below.
              </Typography>

              {error && (
                <Alert severity="error">{error}</Alert>
              )}

              <Controller
                name="code"
                control={resetControl}
                render={({ field: { onChange, value, ref } }) => (
                  <TextField
                    label="Verification Code"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={value || ''}
                    onChange={(e) => {
                      // Only allow numeric input
                      const numericValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(numericValue);
                    }}
                    inputRef={ref}
                    error={!!resetErrors.code}
                    helperText={resetErrors.code?.message}
                    disabled={isLoading}
                    autoFocus
                    placeholder="Enter the 6-digit code from your email"
                    autoComplete="off"
                    name="verification_code_otp"
                    inputProps={{
                      maxLength: 6,
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      autoComplete: 'off',
                      'data-form-type': 'other'
                    }}
                  />
                )}
              />

              <Controller
                name="newPassword"
                control={resetControl}
                render={({ field: { onChange, value, ref } }) => (
                  <TextField
                    label="New Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    value={value || ''}
                    onChange={onChange}
                    inputRef={ref}
                    error={!!resetErrors.newPassword}
                    helperText={resetErrors.newPassword?.message}
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                )}
              />

              <Controller
                name="confirmPassword"
                control={resetControl}
                render={({ field: { onChange, value, ref } }) => (
                  <TextField
                    label="Confirm New Password"
                    type="password"
                    variant="outlined"
                    fullWidth
                    value={value || ''}
                    onChange={onChange}
                    inputRef={ref}
                    error={!!resetErrors.confirmPassword}
                    helperText={resetErrors.confirmPassword?.message}
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                )}
              />

              <Box display="flex" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(0)}
                  disabled={isLoading}
                  fullWidth
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  fullWidth
                  startIcon={isLoading ? <CircularProgress size={20} /> : <Lock />}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </Box>
            </Box>
          </form>
        );

      case 2:
        return (
          <Box display="flex" flexDirection="column" gap={3} textAlign="center">
            <Box display="flex" justifyContent="center" mb={2}>
              <CheckCircle sx={{ fontSize: 64, color: theme.palette.success.main }} />
            </Box>
            
            <Typography variant="h6" color="success">
              Password Reset Successfully!
            </Typography>
            
            <Typography variant="body2" color="textSecondary">
              Your password has been updated. You can now sign in with your new password.
            </Typography>

            <Button
              variant="contained"
              onClick={handleClose}
              fullWidth
            >
              Close
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 500,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="span">
          Reset Password
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box mb={4}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;